import { useState, useCallback, useEffect, useRef } from 'react';
import deliveryOrderService from '../services/deliveryOrderService';
import i18n from '../i18n';

const useDeliveryOrders = (customEndpoint) => {
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Ref to hold the AbortController
    const abortControllerRef = useRef(null);

    const fetchOrders = useCallback(async (params = {}) => {
        // Cancel previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new controller
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        setError(null);
        try {
            const result = await deliveryOrderService.getDeliverySubOrders(params, { 
                signal: controller.signal,
                endpoint: customEndpoint 
            });
            if (result.success) {
                const raw = result.data;
                if (Array.isArray(raw)) {
                    setOrders(raw);
                    setTotal(raw.length);
                } else {
                    setOrders(raw?.items || raw?.data || []);
                    setTotal(raw?.total ?? raw?.count ?? 0);
                }
            } else if (result.isCanceled) {
                // Do not update state if canceled
                return;
            } else {
                setError(result.error);
                setOrders([]);
            }
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.deliveryOrders.fetchList'));
        } finally {
            // Only turn off loading if this is the current request
            if (abortControllerRef.current === controller) {
                setLoading(false);
                abortControllerRef.current = null;
            }
        }
    }, [customEndpoint]);

    const assignSupervisor = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.assignSupervisor(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
                return result;
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const startLoading = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.startLoading(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
                return result;
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const completeLoading = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.completeLoading(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
                return result;
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const generateChallan = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.generateChallan(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
                return result;
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const markInTransit = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.markInTransit(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
                return result;
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const markDelivered = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.markDelivered(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
                return result;
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchOrderById = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await deliveryOrderService.getDeliverySubOrderById(id, {
                endpoint: customEndpoint
            });
            if (result.success) {
                return result;
            } else {
                setError(result.error);
                return result;
            }
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.orders.fetchDetails'));
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [customEndpoint]);

    const addWeightEntry = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.addWeightEntry(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const exportChallans = useCallback(async (params = {}) => {
        try {
            const res = await deliveryOrderService.exportDeliveryChallans(params);
            if (res.success) {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                
                let filename = 'delivery_challans_export.xlsx';
                const contentDisposition = res.headers?.['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                    if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1];
                }
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return { success: true };
            }
            return { success: false, error: res.error };
        } catch (e) {
            return { success: false, error: e.message };
        }
    }, []);

    const resetWeightEntries = useCallback(async (id) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.resetWeightEntries(id);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const markChallanPrinted = useCallback(async (id) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.markChallanPrinted(id);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => o.id === id ? { ...o, ...result.data } : o));
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const editChallan = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const result = await deliveryOrderService.editChallan(id, payload);
            if (result.success && result.data) {
                setOrders(prev => prev.map(o => (String(o.id) === String(id) ? { ...o, ...result.data } : o)));
            }
            return result;
        } catch (e) {
            return { success: false, error: e.message };
        } finally {
            setLoading(false);
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        orders,
        total,
        loading,
        error,
        fetchOrders,
        fetchOrderById,
        assignSupervisor,
        startLoading,
        completeLoading,
        generateChallan,
        markInTransit,
        markDelivered,
        addWeightEntry,
        resetWeightEntries,
        editChallan,
        exportChallans,
        markChallanPrinted,
    };
};

export default useDeliveryOrders;
